import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "../services/authService";
import { Company } from "../types";

interface CompanyContextType {
  company: Company | null;
  setCompany: (company: Company | null) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({
  children,
}) => {
  const [company, setCompanyState] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load company data from localStorage on mount
    loadCompanyData();
  }, []);

  const loadCompanyData = () => {
    setIsLoading(true);
    try {
      const companyData = authService.getCompanyData();
      setCompanyState(companyData);
    } catch (error) {
      console.error("Failed to load company data:", error);
      setCompanyState(null);
    } finally {
      setIsLoading(false);
    }
  };

  const setCompany = (companyData: Company | null) => {
    setCompanyState(companyData);
  };

  const value: CompanyContextType = {
    company,
    setCompany,
    isLoading,
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
